import { useState, useCallback } from "react";
import { toast } from "sonner";
import {
  parseListField,
  validateListItem,
  validateListItems,
  addToList,
  updateListItem,
} from "@/lib/utils/crm/list-field-utils";
import type { UseListFieldEditorParams, UseListFieldEditorResult } from "./types";

/**
 * Encapsulates all state and logic for the ListFieldEditor component.
 * Handles item add/edit/remove, validation, and save.
 */
export function useListFieldEditor({
  value,
  fieldLabel,
  onSave,
  readOnlyMode,
  maxItems,
}: UseListFieldEditorParams): UseListFieldEditorResult {
  const [isSaving, setIsSaving] = useState(false);
  const [items, setItems] = useState<string[]>([]);
  const [newItem, setNewItem] = useState("");
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");

  const initializeEditor = useCallback(() => {
    const parsed = parseListField(value);
    setItems(parsed);
    setNewItem("");
    setEditingIndex(null);
    setValidationErrors({});
  }, [value]);

  const handleAddItem = useCallback(() => {
    const trimmed = newItem.trim();
    if (!trimmed) {
      toast.error("Please enter an item");
      return;
    }
    if (maxItems && items.length >= maxItems) {
      toast.error(`Maximum ${maxItems} items allowed`);
      return;
    }
    const validation = validateListItem(trimmed);
    if (!validation.valid) {
      toast.error(validation.error ?? "Invalid item");
      return;
    }
    if (items.includes(trimmed)) {
      toast.error("This item already exists");
      return;
    }
    setItems((prev) => addToList(prev, trimmed));
    setNewItem("");
    setValidationErrors({});
  }, [newItem, items, maxItems]);

  const handleRemoveItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
    setValidationErrors({});
  }, []);

  const handleStartEdit = useCallback(
    (index: number) => {
      setEditingIndex(index);
      setEditingValue(items[index]);
    },
    [items]
  );

  const handleSaveEdit = useCallback(() => {
    if (editingIndex === null) return;
    const trimmed = editingValue.trim();
    if (!trimmed) {
      toast.error("Item cannot be empty");
      return;
    }
    const validation = validateListItem(trimmed);
    if (!validation.valid) {
      toast.error(validation.error ?? "Invalid item");
      return;
    }
    if (items.some((item, i) => i !== editingIndex && item === trimmed)) {
      toast.error("This item already exists");
      return;
    }
    setItems((prev) => updateListItem(prev, items[editingIndex], trimmed));
    setEditingIndex(null);
    setEditingValue("");
    setValidationErrors({});
  }, [editingIndex, editingValue, items]);

  const handleCancelEdit = useCallback(() => {
    setEditingIndex(null);
    setEditingValue("");
  }, []);

  const validateAllItems = useCallback((): boolean => {
    const validation = validateListItems(items);
    setValidationErrors(validation.errors);
    return validation.valid;
  }, [items]);

  const handleSave = useCallback(
    async (onClose: () => void) => {
      try {
        setIsSaving(true);
        if (!validateAllItems()) {
          toast.error("Please fix validation errors before saving");
          setIsSaving(false);
          return;
        }
        if (!readOnlyMode) {
          await onSave(items);
        }
        toast.success(`${fieldLabel} saved successfully`);
        onClose();
      } catch (err: unknown) {
        console.error(`Error saving ${fieldLabel}:`, err);
        const errorMessage =
          err instanceof Error ? err.message : `Failed to save ${fieldLabel}`;
        toast.error(errorMessage);
      } finally {
        setIsSaving(false);
      }
    },
    [items, onSave, readOnlyMode, validateAllItems, fieldLabel]
  );

  const handleCancel = useCallback(() => {
    setItems([]);
    setNewItem("");
    setEditingIndex(null);
    setEditingValue("");
    setValidationErrors({});
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (editingIndex !== null) {
          handleSaveEdit();
        } else {
          handleAddItem();
        }
      }
    },
    [editingIndex, handleAddItem, handleSaveEdit]
  );

  return {
    items,
    newItem,
    setNewItem,
    validationErrors,
    editingIndex,
    editingValue,
    setEditingValue,
    isSaving,
    initializeEditor,
    handleAddItem,
    handleRemoveItem,
    handleStartEdit,
    handleSaveEdit,
    handleCancelEdit,
    handleSave,
    handleCancel,
    handleKeyDown,
  };
}
